import React, {useCallback, useEffect, useState} from 'react';
import {useOutletContext} from "react-router";
import {Stack, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import {SeasonPicker} from "../components/pickers/SeasonPicker";
import {DraftBoardGrid} from "../components/grids/DraftBoardGrid";
import {RFAsGrid} from "../components/grids/RFAsGrid";
import {DraftBiddingDialog} from "../components/dialogs/DraftBiddingDialog";
import {NewBidGroupDialog} from "../components/dialogs/NewBidGroupDialog";
import {AvailablePlayerGrid} from "../components/grids/AvailablePlayerGrid";
import {DraftManagersTable} from "../components/tables/DraftManagersTable";
import {config} from "../config";
import {apiGet, apiPost, isUserAdmin} from "../utils";

export const DraftContainer = () => {
    const {currentLeague, currentAuction} = useOutletContext();
    const [isDraft, setIsDraft] = useState(false);
    const [isPreDraftOrDraft, setIsPreDraftOrDraft] = useState(false);
    const [draftIsOver, setDraftIsOver] = useState(false);
    const [selectedSeasonYear, setSelectedSeasonYear] = useState(null);
    const [rosterData, setRosterData] = useState({});
    const [nominatingManager, setNominatingManager] = useState(config.managerNames[0]);
    const [openRFAs, setOpenRFAs] = useState([]);
    const [bidGroups, setBidGroups] = useState([]);
    const [openNewBidGroup, setOpenNewBidGroup] = useState(false);
    const [availablePlayers, setAvailablePlayers] = useState([]);
    const [nominatedPlayer, setNominatedPlayer] = useState(null); //TODO: redundant with biddingRow?
    const [winningManagerName, setWinningManagerName] = useState(null);
    const [openBidding, setOpenBidding] = useState(false);
    const [biddingRow, setBiddingRow] = useState({});
    const [bidAmount, setBidAmount] = useState(0);
    const [bidError, setBidError] = useState(false);
    const [helperText, setHelperText] = useState('');
    const [loadingManagerTable, setLoadingManagerTable] = useState(true);
    const [loadingDraftBoard, setLoadingDraftBoard] = useState(true);

    const incrementNominatingManager = (lastNominatingManager) => {
        const lastNominatingManagerName = typeof lastNominatingManager === "string" ? lastNominatingManager : nominatingManager;
        const hasRemainingRosterSpots = (managerName) => {
            return rosterData[managerName] ? config.maxRosterSize - rosterData[managerName].rosterSize > 0 : false;
        };

        const hasRemainingBudget = (managerName) => {
            return rosterData[managerName] ? rosterData[managerName].available > 0 : false;
        };

        let nextIndex = config.managerNames.indexOf(lastNominatingManagerName);
        for (let i = 0; i < config.managerNames.length; i++) {
            nextIndex = nextIndex === config.managerNames.length - 1 ? 0 : nextIndex + 1;
            if (hasRemainingRosterSpots(config.managerNames[nextIndex]) && hasRemainingBudget(config.managerNames[nextIndex])) {
                break;
            }
        }
        if (config.managerNames[nextIndex] === lastNominatingManagerName) {
            console.log('Worst Draft Ever');
        }
        setNominatingManager(config.managerNames[nextIndex]);
    };

    const determineNextNominatingManager = () => {
        if(Object.keys(rosterData).length > 0 && bidGroups.length > 0) {
            const mostRecentBidGroup = bidGroups.at(-1);
            const lastNominatingManager = mostRecentBidGroup.nominating_manager;
            incrementNominatingManager(lastNominatingManager);
        }
    };

    const fetchAllRosterData = useCallback(() => {
        const results = {};
        config.managerNames.forEach((managerName) => {
            fetchRosterData(managerName, (bankData) => {
                results[managerName] = bankData.data;
                setRosterData(results);
            });
        });
    }, []);

    const fetchRosterData = (managerName, callback) => {
        setLoadingManagerTable(true);
        apiGet(`contract/bank/${managerName}`, (bankData) => {
            callback(bankData);
            setLoadingManagerTable(false);
        });
    };

    const fetchOpenRFAs = () => {
        apiGet(`contract/rfa`, (data) => {
            setOpenRFAs(data.data ? data.data : []);
        });
    };

    const fetchDrafted = () => {
        if (selectedSeasonYear) {
            setLoadingDraftBoard(true);
            const draftedGridData = []; //TODO... why is this out here?
            apiGet(`auction/groups/season/${selectedSeasonYear}/week/0`, (bidGroupData) => {
                if (bidGroupData.data) draftedGridData.push(...bidGroupData.data);
                const openBidGroup = bidGroupData.data.filter(bg => bg.bid_group_is_open)[0];
                if (openBidGroup) setNominatedPlayer(openBidGroup);
                for (const bidGroup of draftedGridData) {
                    if (openRFAs.length > 0) {
                        const openRFA = openRFAs.filter(p => p.yahoo_id === bidGroup.yahoo_id);
                        if (openRFA && openRFA.length > 0) {
                            bidGroup.manager_name = openRFA[0].manager_name;
                            bidGroup.is_rfa = true;
                        }
                    }
                }
                setBidGroups(draftedGridData);
                setLoadingDraftBoard(false);
            });
        }
    };

    const fetchAvailablePlayers = () => {
        apiGet(`player/available`, (data) => {
            setAvailablePlayers(data.data);
        });
    };

    useEffect(() => {
        setIsDraft(currentLeague.season_phase === 'DRAFT');
        setIsPreDraftOrDraft(['PREDRAFT', 'DRAFT'].includes(currentLeague.season_phase));
        setSelectedSeasonYear(currentAuction.season_year);
        fetchOpenRFAs();
    }, [currentLeague, currentAuction]);

    useEffect(() => {
        fetchDrafted();
        // eslint-disable-next-line
    }, [selectedSeasonYear]);

    useEffect(() => {
        fetchAllRosterData();
        fetchAvailablePlayers();
    }, [fetchAllRosterData]);

    useEffect(() => {
        determineNextNominatingManager();
        // eslint-disable-next-line
    }, [rosterData, bidGroups]);

    const refresh = () => {
        fetchAllRosterData();
        fetchDrafted();
        fetchOpenRFAs();
        fetchAvailablePlayers();
    };

    const setYear = (year) => {
        setSelectedSeasonYear(year);
    };

    const handleDropRemainingRFAs = () => {
        //TODO: confirmation popup
        apiPost(`contract/drop/rfas`, {}, () => {
            refresh();
        });
    };

    const handleOpenNewBidGroup = () => {
        fetchAvailablePlayers();
        setOpenNewBidGroup(true);
    };

    const handleCloseNewBidGroup = () => {
        setOpenNewBidGroup(false);
    };

    const onPlayerChange = (event, value) => {
        setNominatedPlayer(value);
    };

    const onManagerChange = (event, value) => {
        setWinningManagerName(value);
    };

    const submitBidGroup = (yahooId) => {
        const playerYahooId = typeof yahooId === "number" ? yahooId : nominatedPlayer ? nominatedPlayer.yahoo_id : null;
        setLoadingDraftBoard(true);
        setOpenNewBidGroup(false);
        if (playerYahooId) {
            const nominationPostBody = {
                playerYahooId: playerYahooId,
                managerName: nominatingManager
            }
            apiPost(`auction/nominate`, nominationPostBody, () => {
                fetchDrafted();
            });
        }
    };

    const nominateRFA = (row) => {
        setNominatedPlayer(row);
        setBiddingRow(row);
        setBidAmount(1);
        submitBidGroup(row.yahoo_id);
    };

    const handleOpenBidding = (row) => {
        setNominatedPlayer(row);
        setBiddingRow(row);
        setBidError(false);
        setHelperText('');
        setBidAmount(1);
        setOpenBidding(true);
    };

    const handleCloseBidding = () => {
        setOpenBidding(false);
    };

    const onBidAmountChange = (event) => {
        //TODO: any bid amount validation here? at least non-negative?
        const value = event.target.value;
        setBidAmount(+value);
        // setBidError(false);
        // setHelperText('');
    };

    const submitBid = () => {
        setLoadingDraftBoard(true);
        setOpenNewBidGroup(false);
        setOpenBidding(false);
        if (nominatedPlayer && nominatedPlayer.yahoo_id) {
            const enterDraftPickupPostBody = {
                auctionKey: currentAuction.key,
                bidGroupId: biddingRow.id,
                playerYahooId: nominatedPlayer.yahoo_id,
                managerName: winningManagerName,
                bidAmount: bidAmount,
            }
            if (nominatedPlayer.is_rfa) enterDraftPickupPostBody.rfaOwnerName = nominatedPlayer.manager_name
            apiPost(`auction/draftpicklive`, enterDraftPickupPostBody, (resp) => {
                setNominatedPlayer(null);
                refresh();
            });
        }
    };

    const closeAuction = () => {
        const closePostBody = {
            auctionKey: currentAuction.key
        }
        apiPost(`auction/close`, closePostBody, () => {
            setDraftIsOver(true);
            fetchDrafted();
        });
    };

    const nominationDisabled = () => {
        if (isDraft && !isUserAdmin()) return true;
        const someBidGroupIsOpen = bidGroups.reduce((anyOpen, g) => anyOpen || g.bid_group_is_open, false);
        return !isDraft || openRFAs.length > 0 || someBidGroupIsOpen;
    };

    return (
        <Grid container justifyContent="center">
            <div>
                {isDraft &&
                    <DraftManagersTable loading={loadingManagerTable} rosterData={rosterData}
                                        nominatingManager={nominatingManager}
                                        incrementNominatingManager={incrementNominatingManager}
                                        refreshPage={refresh} draftIsOver={draftIsOver}/>
                }
                <Stack direction="row" spacing={6}>
                    <Stack>
                        {openRFAs.length > 0 &&
                            <div style={{marginTop: 30, marginBottom: 30}}>
                                <Typography variant="h6" color="inherit" display="inline" style={{marginLeft: 10}}>
                                    Open {currentAuction.season_year} RFAs
                                </Typography>
                                {isUserAdmin() &&
                                    <Button variant="contained" color="primary" display="inline"
                                            style={{marginLeft: 28, height: 25}} disabled={!isDraft}
                                            onClick={handleDropRemainingRFAs}>Drop Remaining</Button>}
                                <RFAsGrid rows={openRFAs} loading={null} nominatedPlayer={nominatedPlayer}
                                          onNominateButtonClick={nominateRFA} isDraft={isDraft}/>
                            </div>
                        }
                        <div>
                            <Typography variant="h6" color="inherit" display="inline" style={{marginLeft: 10}}>
                                Draft Board
                            </Typography>
                            <Button variant="contained" color="primary" display="inline"
                                    style={{margin: 28, height: 25}} disabled={nominationDisabled()}
                                    onClick={handleOpenNewBidGroup}>Nominate</Button>
                            {isUserAdmin() &&
                                <Button variant="contained" color="primary" display="inline" style={{height: 25}}
                                        disabled={!isDraft || openRFAs.length > 0}
                                        onClick={closeAuction}>Close Draft</Button>}
                            <div style={{float: "right", marginTop: 10}}>
                                <SeasonPicker setterFunction={setYear}/>
                            </div>
                            {loadingDraftBoard && <LinearProgress color="secondary"/>}
                            <DraftBoardGrid rows={bidGroups} onBiddingButtonClick={handleOpenBidding}/>
                        </div>
                    </Stack>
                    {isPreDraftOrDraft &&
                        <div style={{marginTop: 20, marginBottom: 30}}>
                            <Typography variant="h6" color="inherit" display="inline" style={{marginLeft: 10}}>
                                Available Players
                            </Typography>
                            <AvailablePlayerGrid rows={availablePlayers}/>
                        </div>
                    }
                </Stack>
                <NewBidGroupDialog open={openNewBidGroup} handleClose={handleCloseNewBidGroup}
                                   playerList={availablePlayers} onPlayerChange={onPlayerChange}
                                   onSubmitBidGroup={submitBidGroup} hideAutoBidding/>
                <DraftBiddingDialog open={openBidding} handleClose={handleCloseBidding} player={biddingRow}
                                    onManagerChange={onManagerChange} onBidAmountChange={onBidAmountChange}
                                    bidError={bidError} helperText={helperText}
                                    onSubmitBid={submitBid}/>
            </div>
        </Grid>
    );
}